import React from 'react'
import './App.css'
import './theme/globalStyles.css'
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Button,
  Tooltip,
  Divider,
  ListSubheader,
  Badge
} from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from './context/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from './apollo/client';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Link as RouterLink, 
  Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { 
  Logout as LogoutIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import LoginPage from './pages/LoginPage';
import { lazy, Suspense } from 'react';

// Lazy load all major route components
const WorkflowStudioPage = lazy(() => import('./pages/WorkflowStudioPage'));
const ApiTestingPage = lazy(() => import('./pages/ApiTestingPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const SystemDocumentationPage = lazy(() => import('./pages/SystemDocumentationPage'));
const SystemSourcesPage = lazy(() => import('./pages/SystemSourcesPage').then(module => ({ default: module.SystemSourcesPage })));
const WorkflowDetailsPage = lazy(() => import('./pages/WorkflowDetailsPage'));
const EnhancedClinicalItemWorklistPage = lazy(() => import('./pages/EnhancedClinicalItemWorklistPage'));
const FactManagementPage = lazy(() => import('./pages/UnifiedFactManagementPage'));
const FacilityGroupsPage = lazy(() => import('./pages/FacilityGroupsPage'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const IntegrationHub = lazy(() => import('./pages/IntegrationHub').then(module => ({ default: module.IntegrationHub })));
const IntegrationAnalyticsPage = lazy(() => import('./pages/IntegrationAnalyticsPage').then(module => ({ default: module.IntegrationAnalyticsPage })));
const ReconciliationDashboard = lazy(() => import('./components/reconciliation').then(module => ({ default: module.ReconciliationDashboard })));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard'));
const DashboardHub = lazy(() => import('./pages/DashboardHub'));
import HomeIcon from '@mui/icons-material/Home';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ScienceIcon from '@mui/icons-material/Science';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArticleIcon from '@mui/icons-material/Article';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SchemaIcon from '@mui/icons-material/Schema';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssessmentIcon from '@mui/icons-material/Assessment';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { useAuth } from './hooks/useAuth';
import { useState } from 'react';
import { GuidedTourCustom } from './components/GuidedTourCustom';
import { ComponentTourProvider } from './components/ComponentTourProvider';
import { TenantProvider } from './contexts/TenantContext';
import TenantSwitcher from './components/TenantSwitcher';

const AIWorkflowDesignerPage = lazy(() => import('./pages/AIWorkflowDesignerPage'));

const drawerWidth = 260;

// Loading component for lazy loaded routes
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <CircularProgress />
  </Box>
);

// Navigation item type
interface NavigationItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  roles: string[];
  badge?: string;
}

// Navigation structure with categories
const navigationStructure: Record<string, NavigationItem[]> = {
  main: [
    { text: 'Home', path: '/', icon: <HomeIcon />, roles: ['resident', 'supervisor', 'admin'] },
    { text: 'Dashboard Hub', path: '/dashboards', icon: <DashboardIcon />, roles: ['supervisor', 'admin'], badge: 'new' },
    { text: 'Worklist', path: '/worklist', icon: <ListAltIcon />, roles: ['resident', 'supervisor', 'admin'] },
  ],
  workflow: [
    { text: 'Workflow Studio', path: '/studio', icon: <DesignServicesIcon />, roles: ['supervisor', 'admin'] },
    { text: 'AI Designer', path: '/ai-designer', icon: <SmartToyIcon />, roles: ['supervisor', 'admin'], badge: 'ai' },
    { text: 'Fact Management', path: '/fact-management', icon: <SchemaIcon />, roles: ['supervisor', 'admin'] },
    { text: 'Facility Groups', path: '/facility-groups', icon: <BusinessIcon />, roles: ['supervisor', 'admin'] },
  ],
  system: [
    { text: 'System Sources', path: '/system-sources', icon: <StorageIcon />, roles: ['supervisor', 'admin'] },
    { text: 'Data Reconciliation', path: '/reconciliation', icon: <IntegrationInstructionsIcon />, roles: ['supervisor', 'admin'] },
    { text: 'Documentation', path: '/documentation', icon: <ArticleIcon />, roles: ['supervisor', 'admin'] },
    { text: 'Integration Hub', path: '/integration-hub', icon: <IntegrationInstructionsIcon />, roles: ['supervisor', 'admin'] },
  ],
  admin: [
    { text: 'Admin Dashboard', path: '/admin', icon: <DashboardIcon />, roles: ['admin'] },
    { text: 'Operations', path: '/operations', icon: <DashboardIcon />, roles: ['supervisor', 'admin'] },
    { text: 'Analytics', path: '/analytics', icon: <AssessmentIcon />, roles: ['supervisor', 'admin'] },
    { text: 'API Testing', path: '/api-testing', icon: <ScienceIcon />, roles: ['admin'] },
  ],
};

// Get all navigation items as a flat list
const getAllNavItems = (userRole: string | undefined) => {
  const allItems = [
    ...navigationStructure.main,
    ...navigationStructure.workflow,
    ...navigationStructure.system,
    ...navigationStructure.admin,
  ];
  
  return allItems.filter(item => 
    !userRole || item.roles.includes(userRole)
  );
};

// Get navigation items by category
const getNavItemsByCategory = (userRole: string | undefined) => {
  const filterByRole = (items: typeof navigationStructure.main) => 
    items.filter(item => !userRole || item.roles.includes(userRole));

  return {
    main: filterByRole(navigationStructure.main),
    workflow: filterByRole(navigationStructure.workflow),
    system: filterByRole(navigationStructure.system),
    admin: filterByRole(navigationStructure.admin),
  };
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles }: { children: React.ReactNode, requiredRoles?: string[] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && !requiredRoles.includes(user.role)) {
    return (
      <Box p={3}>
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to access this page. Required role: {requiredRoles.join(' or ')}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

// User Menu Component
const UserMenu = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <AdminIcon fontSize="small" />;
      case 'supervisor': return <SupervisorIcon fontSize="small" />;
      default: return <PersonIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (role) {
      case 'admin': return 'error';
      case 'supervisor': return 'secondary';
      default: return 'primary';
    }
  };

  if (!user) return null;

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          icon={getRoleIcon(user.role)}
          label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
          color={getRoleColor(user.role)}
          size="small"
          sx={{ 
            height: 24,
            '& .MuiChip-label': { px: 1 }
          }}
        />
        <IconButton
          size="small"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenuOpen}
          color="inherit"
          sx={{ p: 0.5 }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {user.name.charAt(0)}
          </Avatar>
        </IconButton>
      </Box>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">{user.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

// Navigation Dropdown Component
const NavigationDropdown = ({ items, label }: { items: NavigationItem[], label: string }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const isActive = items.some(item => location.pathname === item.path);

  if (items.length === 0) return null;

  return (
    <>
      <Button
        color="inherit"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: isActive ? 600 : 500,
          borderRadius: '20px',
          px: 2,
          py: 0.5,
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { mt: 1 }
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleNavigate(item.path)}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                color={item.badge === 'ai' ? 'secondary' : 'primary'}
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Quick Access Button
const QuickAccessButton = ({ item }: { item: NavigationItem }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === item.path;

  return (
    <Tooltip title={item.text}>
      <IconButton
        color="inherit"
        onClick={() => navigate(item.path)}
        sx={{
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {item.badge ? (
          <Badge 
            badgeContent={item.badge} 
            color={item.badge === 'ai' ? 'secondary' : 'error'}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 9,
                height: 16,
                minWidth: 16,
              }
            }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </IconButton>
    </Tooltip>
  );
};

// Main App Layout Component
const AppLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navCategories = getNavItemsByCategory(user?.role);
  const allNavItems = getAllNavItems(user?.role);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {/* Logo and Title */}
          <Box display="flex" alignItems="center" gap={1}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="start"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {!isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{ mr: 1 }}
              >
                <AppsIcon />
              </IconButton>
            )}
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #a10693 0%, #2185f2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: 'none',
              }}
            >
              {isMobile ? 'OSW' : 'OS Workflow'}
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && !isTablet && (
            <Box display="flex" gap={1} flex={1} ml={2}>
              {navCategories.main.map(item => (
                <Button
                  key={item.path}
                  color="inherit"
                  component={RouterLink}
                  to={item.path}
                  data-tour={`nav-${item.path.replace('/', '')}`}
                  sx={{
                    textTransform: 'none',
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    borderRadius: '20px',
                    px: 2,
                    py: 0.5,
                    backgroundColor: location.pathname === item.path ? 'action.selected' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {item.text}
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      color="secondary"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </Button>
              ))}
              {navCategories.workflow.length > 0 && (
                <NavigationDropdown
                  items={navCategories.workflow}
                  label="Workflow"
                />
              )}
              {navCategories.system.length > 0 && (
                <NavigationDropdown
                  items={navCategories.system}
                  label="System"
                />
              )}
              {navCategories.admin.length > 0 && (
                <NavigationDropdown
                  items={navCategories.admin}
                  label="Admin"
                />
              )}
            </Box>
          )}

          {/* Tablet Quick Access Icons */}
          {!isMobile && isTablet && (
            <Box display="flex" gap={0.5} flex={1} ml={2}>
              {navCategories.main.map(item => (
                <QuickAccessButton key={item.path} item={item} />
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
              {[...navCategories.workflow, ...navCategories.system, ...navCategories.admin]
                .slice(0, 4)
                .map(item => (
                  <QuickAccessButton key={item.path} item={item} />
                ))}
            </Box>
          )}

          {/* Spacer */}
          <Box flex={1} />

          {/* Notifications */}
          <IconButton 
            color="inherit" 
            component={RouterLink} 
            to="/notifications"
            size="small"
            sx={{ p: 1 }}
          >
            <NotificationsIcon />
          </IconButton>

          {/* Theme Toggle */}
          <ThemeToggle />
          <TenantSwitcher variant="compact" />
          
          {/* User Menu */}
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: '85%',
            maxWidth: 320,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Navigation</Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navCategories.main.length > 0 && (
            <>
              <ListSubheader>Main</ListSubheader>
              {navCategories.main.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color={item.badge === 'ai' ? 'secondary' : 'primary'}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
          {navCategories.workflow.length > 0 && (
            <>
              <ListSubheader>Workflow</ListSubheader>
              {navCategories.workflow.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color={item.badge === 'ai' ? 'secondary' : 'primary'}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
          {navCategories.system.length > 0 && (
            <>
              <ListSubheader>System</ListSubheader>
              {navCategories.system.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
          {navCategories.admin.length > 0 && (
            <>
              <ListSubheader>Administration</ListSubheader>
              {navCategories.admin.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
        </List>
      </Drawer>

      {/* Desktop Sidebar (collapsible) */}
      {!isMobile && (
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            width: sidebarOpen ? drawerWidth : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: '64px',
              height: 'calc(100% - 64px)',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          <Box sx={{ overflow: 'auto' }}>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Quick Access" 
                  primaryTypographyProps={{ 
                    variant: 'subtitle2', 
                    color: 'text.secondary',
                    fontWeight: 'bold' 
                  }} 
                />
              </ListItem>
              {allNavItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={location.pathname === item.path}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color={item.badge === 'ai' ? 'secondary' : 'primary'}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      )}

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 1, sm: 2, md: 3 },
          ml: !isMobile && sidebarOpen ? `${drawerWidth}px` : 0,
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        

        <Container maxWidth={false} sx={{ px: { xs: 0, sm: 2 } }}>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Box>
                  <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                    Welcome to OS Workflow
                  </Typography>
                  <Typography variant="h6" gutterBottom color="text.secondary">
                    Hello, {user?.name}! You're logged in as a <Chip label={user?.role} size="small" color="primary" />
                  </Typography>
                  <Box mt={4}>
                    <Typography variant="h5" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
                      {navCategories.main.map((item) => (
                        <Button
                          key={item.path}
                          variant="contained"
                          size="large"
                          startIcon={item.icon}
                          component={RouterLink}
                          to={item.path}
                          sx={{ 
                            minWidth: 200,
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                          }}
                        >
                          {item.text}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </ProtectedRoute>
            } />
            <Route path="/studio" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <WorkflowStudioPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/worklist" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EnhancedClinicalItemWorklistPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboardPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/api-testing" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<PageLoader />}>
                  <ApiTestingPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/documentation" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <SystemDocumentationPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/system-sources" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <SystemSourcesPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/fact-management" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <FactManagementPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/facility-groups" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <FacilityGroupsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/integration-hub" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <IntegrationHub />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/integration-analytics" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <IntegrationAnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <NotificationCenter />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/operations" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <OperationsDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboards" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <DashboardHub />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <UserProfile />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/workflow/:workflowId" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WorkflowDetailsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/ai-designer" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AIWorkflowDesignerPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/reconciliation" element={
              <ProtectedRoute requiredRoles={['supervisor', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <ReconciliationDashboard 
                    onError={(error) => console.error('Reconciliation error:', error)}
                    onSuccess={(message) => console.log('Reconciliation success:', message)}
                  />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/login" element={<LoginPage />} />
          </Routes>
        </Container>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider>
        <Router>
          <TenantProvider>
            <SnackbarProvider 
              maxSnack={3}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              autoHideDuration={3000}
            >
              <ComponentTourProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/*" element={<AppLayout />} />
              </Routes>
                <GuidedTourCustom />
              </ComponentTourProvider>
            </SnackbarProvider>
          </TenantProvider>
        </Router>
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;
